import { useCallback, useState } from "react";
// Components
import { Grid, Theme, Tooltip, Button } from "@mui/material";
import { useTheme } from "@mui/styles";
// API Calls
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { TextField } from "src/components/FormikComponents/TextField";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { CopyToClipboard } from "src/pages/TimelineEventEditors/helpers";

interface IAPIKeyWindowProps {
  id: string;
  path: string[];
}

const APIKeyStyles = (theme: Theme): any => ({
  backgroundColor: theme.custom.timeline.contextListBackground,
  width: "100%",
  "& .api_key.disabled .input_control div": { opacity: 1 },
  "& .mask_toggle, & .copy_to_clipboard": {
    cursor: "pointer",
    pointerEvents: "fill",
    width: 20,
    height: 20,
    margin: "5px 2px",
    "& .svg": { width: 20, height: 20 },
  },
  "& .reset_key_btn": { marginTop: "10px", marginLeft: "95px", width: 140 },
});

/**
 * Display API Version Info Window
 */
export const APIKeyWindow = (props: IAPIKeyWindowProps): JSX.Element => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [apiKey, setAPIKey] = useState<string | undefined>(undefined);
  const [keyIsMasked, setKeyIsMasked] = useState<boolean>(true);

  const toggleKeyMask = (): void => {
    setKeyIsMasked((prevState: boolean) => !prevState);
  };

  const fetchAPIKey = useCallback(
    async (reset = false) => {
      setIsFetching(true);
      const notifications: INotifications = reset
        ? {
            dispatch: dispatch,
            infoMessage: "Resetting API Key...",
            successMessage: "API Key Successfully Reset.",
            errorMessage: "Error: Unable to reset API Key",
          }
        : {};
      const response = await AmplifyAPIWrapper.Get<{ jwt: string }>(
        `/user/apiToken${reset ? `/${true}` : ""}`,
        notifications
      );
      setAPIKey(response?.jwt);
      setIsFetching(false);
    },
    [dispatch]
  );

  useEffect(() => {
    fetchAPIKey();
  }, [fetchAPIKey]);

  const handleClick = (): void => {
    fetchAPIKey(true);
  };

  return (
    <Grid container sx={APIKeyStyles(theme)} direction="column" wrap="nowrap" alignItems="flex-start">
      <SpinnerProgress className="loading_spinner" loading={isFetching} message="Loading API Key" />
      <Grid container direction="column" style={{ padding: "20px 0px" }}>
        <Grid container style={{ width: "auto" }}>
          <Grid item style={{ width: 600 }}>
            <TextField
              className="api_key"
              name="apiKey"
              label={"API Key"}
              value={apiKey}
              disabled={true}
              type={keyIsMasked ? "password" : "text"}
              multiline={keyIsMasked ? false : true}
              minRows={2}
              maxRows={5}
            />
          </Grid>
          <Tooltip enterDelay={0} placement="top" title={keyIsMasked ? "Reveal Key" : "Hide Key"}>
            {keyIsMasked ? (
              <Visibility className="mask_toggle" onClick={toggleKeyMask} />
            ) : (
              <VisibilityOff className="mask_toggle" onClick={toggleKeyMask} />
            )}
          </Tooltip>
          <CopyToClipboard copyValue={apiKey ?? ""} className="copy_to_clipboard" />
        </Grid>

        <Button
          className="reset_key_btn"
          variant="outlined"
          color="primary"
          disabled={isFetching}
          onClick={handleClick}
        >
          Reset API Key
        </Button>
      </Grid>
    </Grid>
  );
};
